// src/belediye/components/mesajlasma/EkGrid.js
// FAZ-55/19 — EK GÖSTERİMİNİN TEK KAYNAĞI.
//
// Bu dört bileşen (ChatImage / FilePill / AttachmentGrid / Lightbox) eskiden
// ChatThread.js'in içinde yaşıyordu ve YALNIZ sohbet baloncuğunda kullanılabiliyordu.
// Kullanıcı (2026-07-28): "hızlı görevde de normal görevde de bir başvuruya
// fotoğraf eklenince ilk mesajda bu fotoğraf görünmüyor; burada görünmeyecekse
// zaten nerede görünecek? … harita nasıl gidiyorsa fotoğraf da gitsin, aynı
// mantıkta çok fotoğraf az fotoğraf ya da belge."
//
// Görev kartı (RefMessageCard) ayrı bir dosya olduğu için kuralı oraya KOPYALAMAK
// gerekirdi; iki kopya demek, "üç resim yan yana" kuralının bir gün yalnız bir
// yerde değişmesi demek. O yüzden ortak modül: baloncuk da, görev kartı da,
// teslim/düzeltme blok kartı da AYNI ızgarayı çağırır.
//
// Not: ekler HAM /media/ yolundan DEĞİL, izin denetimli indirme ucundan gelir
// (Bearer'lı blob → objectURL). Bu yüzden <img src> doğrudan URL alamaz.
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowDownTrayIcon, DocumentIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

import api from "../../services/belediyeApi";
import { toApiRelative } from "../../services/mesajlasmaApi";

const resimMi = (a) => (a?.mime_type || "").startsWith("image/");

// Gated indirme → tarayıcı indirmesi. `download_url` göreli API yoludur;
// toApiRelative olmadan çift /api oluşur (bkz. download_url çift /api tuzağı).
async function indir(att) {
  try {
    const r = await api.get(toApiRelative(att.download_url), { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = att.filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Dosya indirilemedi.");
  }
}

export function FilePill({ att, kutu = false }) {
  return (
    <button
      type="button"
      className={`msj-file ${kutu ? "msj-file--kutu" : ""}`}
      onClick={() => indir(att)}
      title="İndir"
    >
      <DocumentIcon /> <span className="msj-file-ad">{att.filename}</span>
    </button>
  );
}

// Korumalı görsel: gated download → Bearer'lı blob → objectURL; tıkla →
// lightbox (onOpen) — WhatsApp gibi. İndirme yetkisi yoksa (403) dosya-hapına
// düşer: kırık resim ikonu göstermektense indirilebilir bir satır göster.
export function ChatImage({ att, onOpen, kucuk = false }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    api
      .get(`${toApiRelative(att.download_url)}?inline=1`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        revoke = URL.createObjectURL(r.data);
        setSrc(revoke);
      })
      .catch(() => setSrc(false));
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [att.download_url]);
  if (src === false) return <FilePill att={att} kutu={kucuk} />;
  if (!src)
    return (
      <div
        className={`msj-img ${kucuk ? "msj-img--kucuk" : ""}`}
        style={{ background: "var(--color-bg-soft)", height: kucuk ? undefined : 120 }}
      />
    );
  return (
    <img
      className={`msj-img ${kucuk ? "msj-img--kucuk" : ""}`}
      src={src}
      alt={att.filename}
      onClick={() => (onOpen ? onOpen(att, src) : window.open(src, "_blank", "noopener"))}
    />
  );
}

// FAZ-55/16 — EK IZGARASI. Kullanıcı: "üç resim varsa peş peşe alt alta
// sıralama, saçma oluyor; WhatsApp'taki gibi aynı kutuda küçük kareler olsun;
// tek resimse büyük kalabilir. Belgeler için de: bir belge tam genişlik, iki
// belge yarı yarıya, üç belge kare kutucuk."
// Kural tek yerde: RESİMLER kendi ızgarasında, BELGELER kendi ızgarasında;
// sütun sayısı adede göre (1 → tam, 2 → iki, 3+ → üç).
export function AttachmentGrid({ atts, onOpenImage }) {
  const liste = atts || [];
  if (liste.length === 0) return null;
  const resimler = liste.filter(resimMi);
  const belgeler = liste.filter((a) => !resimMi(a));
  const kolon = (n) => (n <= 1 ? 1 : n === 2 ? 2 : 3);
  return (
    <>
      {resimler.length > 0 &&
        (resimler.length === 1 ? (
          <ChatImage att={resimler[0]} onOpen={(att, src) => onOpenImage?.(att, src, resimler)} />
        ) : (
          <div className="msj-ekgrid msj-ekgrid--img" style={{ "--kolon": kolon(resimler.length) }}>
            {resimler.map((a) => (
              <ChatImage key={a.id} att={a} kucuk onOpen={(att, src) => onOpenImage?.(att, src, resimler)} />
            ))}
          </div>
        ))}
      {belgeler.length > 0 && (
        <div className="msj-ekgrid msj-ekgrid--doc" style={{ "--kolon": kolon(belgeler.length) }}>
          {belgeler.map((a) => (
            <FilePill key={a.id} att={a} kutu={belgeler.length > 1} />
          ))}
        </div>
      )}
    </>
  );
}

// D1: tam ekran görsel — büyüt + indir + kapat (ESC). Portal ile viewport'a
// (dock transform'undan bağımsız). İndirme yine gated download ucundan.
export function Lightbox({ att, src, onClose, onPrev, onNext, sira = "" }) {
  useEffect(() => {
    // FAZ-55/16: çoklu ek → modalda ileri/geri (kullanıcı: "modal kaydıra
    // kaydıra diğer resimleri görürüz"). Klavye de aynı işi yapar.
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && onNext) onNext();
      if (e.key === "ArrowLeft" && onPrev) onPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onNext, onPrev]);
  // Gezinirken (ileri/geri) yeni görselin blob'u BURADA çözülür — kaynağı
  // baloncuktaki <img>'den taşımak gerekmesin.
  const [gorsel, setGorsel] = useState(src || null);
  useEffect(() => {
    if (src) {
      setGorsel(src);
      return undefined;
    }
    let revoke = null;
    let cancelled = false;
    api
      .get(`${toApiRelative(att.download_url)}?inline=1`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        revoke = URL.createObjectURL(r.data);
        setGorsel(revoke);
      })
      .catch(() => setGorsel(false));
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [att.download_url, src]);
  return createPortal(
    <div className="msj-lightbox" onClick={onClose} role="dialog" aria-modal="true">
      <div className="msj-lightbox-bar" onClick={(e) => e.stopPropagation()}>
        <span className="msj-lightbox-name">
          {att.filename}
          {sira && <small className="msj-lightbox-sira">{sira}</small>}
        </span>
        <button type="button" className="msj-lightbox-btn" onClick={() => indir(att)} title="İndir">
          <ArrowDownTrayIcon /> İndir
        </button>
        <button type="button" className="msj-lightbox-btn" onClick={onClose} aria-label="Kapat">
          <XMarkIcon />
        </button>
      </div>
      {onPrev && (
        <button
          type="button"
          className="msj-lightbox-nav is-prev"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          aria-label="Önceki"
        >
          ‹
        </button>
      )}
      <img className="msj-lightbox-img" src={gorsel || undefined} alt={att.filename} onClick={(e) => e.stopPropagation()} />
      {onNext && (
        <button
          type="button"
          className="msj-lightbox-nav is-next"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          aria-label="Sonraki"
        >
          ›
        </button>
      )}
    </div>,
    document.body
  );
}

export default AttachmentGrid;
